import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import "../../../../assets/css/datatable.css";
import DeleteConfirmation from "../../../../components/modal-popup/delete-popup";
import SubCast from "../../../../models/subcast";
import SubCastFactory from "../../../../factories/subcast";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon, PlusIcon, ListBulletIcon } from "@heroicons/react/24/outline";

const SubCastPage: React.FC = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const navigate = useNavigate();
    const [subCastData, setSubCastData] = useState<SubCast[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const [totalRecords, settotalRecords] = useState(0);
    const { showToast }: any = useToast();
    const [companyToDelete, setCompanyToDelete] = useState<SubCast | null>(null)
    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("createddate");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [sortOrderapi, setSortOrderApi] = useState<string>("asc");
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const actionBodyTemplate = (rowData: SubCast) => {
        return (
            <div className="action-icons flex items-center gap-2 flex-wrap">
                <Button label="Edit" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} />
                <Button label="Delete" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    async function getSubCastData() {
        try {
            let url;
            setDataLoad(true);
            if (name !== undefined && name !== null && name !== "") {
                url = `/subcast/list/${skipData}/${itemsPerPage}/${sortField || "createddate"}/${sortOrderapi}/${name}`;
            } else {
                url = `/subcast/list/${skipData}/${itemsPerPage}/${sortField || "createddate"}/${sortOrderapi}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsPerPage)
                        );
                        settotalPages(totalpage);
                        setSubCastData(SubCastFactory.createFromJsonArray(response.data.data));
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    const confirmDelete = async () => {
        if (companyToDelete) {
            try {
                await apiFunctions
                    .delete(`/subcast/delete/${companyToDelete.id}`)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        errorHandler(err);
                    })
                    .finally(() => {
                        setDeleteConfirmationVisible(false);
                        setName("");
                        setCompanyToDelete(null);
                        setSortField("createddate");
                        setSortOrder(-1);
                        setCurrentPage(0);
                        getSubCastData();
                    });
            } catch (error) {
                console.log(error);
            }
        }
    };

    const handleEdit = (rowData: SubCast) => {
        navigate(`/admin/subcast/edit/${rowData.id}`);
    };

    const handleDelete = (rowData: SubCast) => {
        setCompanyToDelete(rowData);
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setCompanyToDelete(null);
    };

    const onSort = (e: any) => {
        const field = e.sortField;
        const order = e.sortOrder === 1 ? "asc" : "desc";

        setSortOrder(e.sortOrder);
        setSortField(field);
        setSortOrderApi(order);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
        } else {
            setName("");
            setLoad(false);
        }
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getSubCastData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, name, sortField, sortOrder]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getSubCastData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/settings');
    }

    const handleAdd = () => {
        navigate('/admin/subcast/create');
    }

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    const header = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                {/* Title */}
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Sub Cast</h2>
                </div>

                {/* Search + Add Button */}
                <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    {/* Search Box */}
                    <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                        <span className="text-gray-600">Search:</span>
                        <PrimeInputText
                            type="text"
                            className="search-input w-full sm:w-auto"
                            onChange={(e) => getsearchdata(e)}
                        />
                    </div>

                    {/* Add Button */}
                    <div className="round-btn-div">
                        <Button
                            icon={<PlusIcon className="w-6 h-6" />}
                            className="round-button"
                            onClick={handleAdd}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <>
            <div className="p-4 standalone-page-bg">
                <div className="flex items-center gap-2 mb-2 cursor-pointer w-max" onClick={handleBack}>
                    <ArrowLeftIcon className="w-6 h-6 text-gray-700" />
                    <h3 className="m-0 text-lg sm:text-xl">Back</h3>
                </div>
                <div className="table-margin inner-table-margin">
                    {isMobile ? (
                        <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                            <div className="p-3 pb-2">{header}</div>
                            <div className="flex flex-col gap-2 px-3 pb-3">
                                {dataLoad ? (
                                    Array.from({ length: itemsPerPage }, (_, i) => (
                                        <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                            <Skeleton width="60%" height="1rem" className="mb-2" />
                                            <Skeleton width="40%" height="0.8rem" />
                                        </div>
                                    ))
                                ) : subCastData.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "64px 0" }}>
                                        <ListBulletIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                        <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No sub casts found</p>
                                    </div>
                                ) : (
                                    subCastData.map((rowData) => (
                                        <div key={rowData.id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                            <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name}</p>
                                            <p className="m-0 text-xs break-words mt-1" style={{ color: "#9ca3af" }}>{rowData.namegujarati}</p>
                                            <div className="flex justify-end gap-2 mt-2">
                                                {actionBodyTemplate(rowData)}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    ) : (
                        <DataTable
                            scrollable
                            scrollHeight="calc(100vh - 220px)"
                            style={{ minHeight: "calc(100vh - 140px)", background: "#fff" }}
                            header={header}
                            value={
                                dataLoad
                                    ? Array.from({ length: itemsPerPage }, (_, i) => ({ id: `_skeleton_${i}` }))
                                    : subCastData
                            }
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={onSort}
                            resizableColumns
                            removableSort
                            rows={itemsPerPage}
                            emptyMessage={
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <ListBulletIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No sub casts found</p>
                                </div>
                            }
                        >
                            <Column field="name" header="Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.name} />
                            <Column field="namegujarati" header="Gujarati Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.namegujarati} />
                            <Column header="Action" className="action-column" frozen body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                        </DataTable>
                    )}
                    {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                        {/* PrimeReact Paginator */}
                        <Paginator
                            template={paginatoreTemplate}
                            first={startIndex}
                            rows={itemsPerPage}
                            totalRecords={totalRecords}
                        />

                        {/* Custom Pagination Buttons */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                                onClick={() => handlePageChange(currentPage - 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentPage === 0}
                            >
                                <span className="sm:hidden">Prev</span>
                                <span className="hidden sm:inline">Previous</span>
                            </Button>

                            <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                    Page {currentPage + 1} of {totalPages || 1}
                                </span>
                            </Button>

                            <Button
                                onClick={() => handlePageChange(currentPage + 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>}
                </div>
            </div>
            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Confirm Delete"
                message="Are you sure you want to delete this Data?"
            />
        </>
    );
};

export default SubCastPage;
